import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { RefreshCw, Search, MonitorSmartphone, AlertCircle } from "lucide-react";
import { useAdeDevices } from "@/hooks/useAdeDevices";
import { toast } from "sonner";

interface DeviceAssignmentStepProps {
  selectedDevices: string[];
  onSelectDevices: (deviceIds: string[]) => void;
  abmConnectionId: string;
}

export const DeviceAssignmentStep = ({ 
  selectedDevices, 
  onSelectDevices,
  abmConnectionId 
}: DeviceAssignmentStepProps) => {
  const { devices, loading, syncing, syncDevices, getUnassignedDevices } = useAdeDevices();
  const [searchQuery, setSearchQuery] = useState("");

  const availableDevices = devices.filter(d => d.abm_connection_id === abmConnectionId);
  const unassignedDevices = getUnassignedDevices().filter(d => d.abm_connection_id === abmConnectionId);

  const filteredDevices = availableDevices.filter(device =>
    device.serial_number.toLowerCase().includes(searchQuery.toLowerCase()) ||
    device.model?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSyncDevices = async () => {
    try {
      await syncDevices();
      toast.success("Devices synced successfully");
    } catch (error) {
      toast.error("Failed to sync devices");
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      onSelectDevices(filteredDevices.map(d => d.id));
    } else {
      onSelectDevices([]);
    }
  };

  const handleSelectDevice = (deviceId: string, checked: boolean) => {
    if (checked) {
      onSelectDevices([...selectedDevices, deviceId]);
    } else {
      onSelectDevices(selectedDevices.filter(id => id !== deviceId));
    }
  };

  if (!abmConnectionId) {
    return (
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-2">Device Assignment</h2>
          <p className="text-muted-foreground">
            Select devices to assign to this enrollment profile
          </p>
        </div>
        <Card disableHover className="border-amber-500/50 bg-amber-50/50 dark:bg-amber-950/20">
          <CardHeader>
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-500 mt-0.5" />
              <div>
                <CardTitle className="text-lg">No ABM Configuration Selected</CardTitle>
                <CardDescription className="mt-1">
                  Please select an ABM configuration in the previous step to view available devices.
                </CardDescription>
              </div>
            </div>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold mb-2">Device Assignment</h2>
        <p className="text-muted-foreground">
          Select devices to assign to this enrollment profile
        </p>
      </div>

      <Card disableHover>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <MonitorSmartphone className="h-5 w-5 text-primary" />
                Available Devices
              </CardTitle>
              <CardDescription className="mt-1">
                {selectedDevices.length} of {availableDevices.length} devices selected
                {unassignedDevices.length > 0 && (
                  <span className="ml-2">
                    • {unassignedDevices.length} unassigned
                  </span>
                )}
              </CardDescription>
            </div>
            <Button 
              variant="outline" 
              size="sm"
              onClick={handleSyncDevices}
              disabled={syncing}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
              Sync Devices
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by serial number or model..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>

            {loading ? (
              <div className="text-center py-8 text-muted-foreground">
                Loading devices...
              </div>
            ) : filteredDevices.length === 0 ? (
              <div className="text-center py-8">
                <MonitorSmartphone className="h-12 w-12 mx-auto text-muted-foreground/50 mb-3" />
                <p className="text-muted-foreground">
                  {searchQuery ? "No devices match your search" : "No devices available"}
                </p>
                {!searchQuery && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="mt-3"
                    onClick={handleSyncDevices}
                    disabled={syncing}
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Sync from ABM
                  </Button>
                )}
              </div>
            ) : (
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">
                        <Checkbox
                          checked={selectedDevices.length === filteredDevices.length}
                          onCheckedChange={handleSelectAll}
                        />
                      </TableHead>
                      <TableHead>Serial Number</TableHead>
                      <TableHead>Model</TableHead>
                      <TableHead>OS</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredDevices.map((device) => (
                      <TableRow key={device.id}>
                        <TableCell>
                          <Checkbox
                            checked={selectedDevices.includes(device.id)}
                            onCheckedChange={(checked) => 
                              handleSelectDevice(device.id, checked as boolean)
                            }
                          />
                        </TableCell>
                        <TableCell className="font-mono text-sm">
                          {device.serial_number}
                        </TableCell>
                        <TableCell>{device.model || "Unknown"}</TableCell>
                        <TableCell>{device.os || "Unknown"}</TableCell>
                        <TableCell>
                          {device.profile_uuid ? (
                            <Badge variant="secondary">Assigned</Badge>
                          ) : (
                            <Badge variant="outline">Unassigned</Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
